import {
  type DocumentComment,
  SHARE_ACCESS,
  SHARE_ACCESS_LABELS,
  type ShareAccess,
  type ShareLink,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  Link2,
  Loader2,
  MapPin,
  MessageSquare,
  MessageSquarePlus,
  Share2,
  X,
} from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  buildThreads,
  CommentComposer,
  CommentPin,
  type CommentThread,
  CommentThreadView,
  threadLabel,
} from "../components/collab";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Field, Input, Select } from "../components/ui/input";
import { client, orpc } from "../lib/api";
import { useOrgRole } from "../lib/org";
import { cn, errorMessage, formatDate, formatDuration, formatRelative } from "../lib/utils";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";
import { useVideoApi, useVideoMaybe } from "./video/store";

/**
 * Working together in the editor (docs/15-zusammenarbeit.md): comments with
 * pins on the page (or a moment of the video) and share links.
 */

// ---------------------------------------------------------------------------
// Comment UI state
// ---------------------------------------------------------------------------

export interface CommentDraft {
  pageId: string;
  x: number;
  y: number;
  timeMs?: number;
}

export type CommentFilter = "open" | "resolved" | "all";

interface CommentUi {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Next click on the page places a comment. */
  placing: boolean;
  setPlacing: (placing: boolean) => void;
  draft: CommentDraft | null;
  setDraft: (draft: CommentDraft | null) => void;
  active: string | null;
  setActive: (id: string | null) => void;
  filter: CommentFilter;
  setFilter: (filter: CommentFilter) => void;
}

const CommentUiContext = createContext<CommentUi | null>(null);

export function useCommentUiState(initialActive: string | null): CommentUi {
  const [open, setOpen] = useState(Boolean(initialActive));
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState<CommentDraft | null>(null);
  const [active, setActive] = useState<string | null>(initialActive);
  const [filter, setFilter] = useState<CommentFilter>("open");
  // A notification for this file was clicked while it is open (?comment= changed).
  useEffect(() => {
    if (!initialActive) return;
    setActive(initialActive);
    setOpen(true);
  }, [initialActive]);
  // Esc leaves the comment mode.
  useEffect(() => {
    if (!placing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPlacing(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placing]);
  return useMemo(
    () => ({
      open,
      setOpen: (value: boolean) => {
        setOpen(value);
        if (!value) {
          setPlacing(false);
          setDraft(null);
        }
      },
      placing,
      setPlacing,
      draft,
      setDraft,
      active,
      setActive,
      filter,
      setFilter,
    }),
    [open, placing, draft, active, filter],
  );
}

export function CommentUiProvider({ value, children }: { value: CommentUi; children: ReactNode }) {
  return <CommentUiContext.Provider value={value}>{children}</CommentUiContext.Provider>;
}

/** Null outside the editor (e.g. previews). */
export function useCommentUi(): CommentUi | null {
  return useContext(CommentUiContext);
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

function useDocumentComments() {
  const env = useEnv();
  const queryClient = useQueryClient();
  const input = { orgId: env.orgId, documentId: env.documentId };
  const query = useQuery({
    ...orpc.comments.list.queryOptions({ input }),
    // Others comment too (also guests of a link): keep the list fresh.
    refetchInterval: 20_000,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: orpc.comments.list.key({ input }) });
    void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const create = useMutation({
    mutationFn: (payload: {
      body: string;
      parentId?: string;
      pageId?: string;
      x?: number;
      y?: number;
      timeMs?: number;
    }) => client.comments.create({ ...input, ...payload }),
    onSuccess: refresh,
    onError,
  });
  const update = useMutation({
    mutationFn: (payload: { commentId: string; body?: string; resolved?: boolean }) =>
      client.comments.update({ orgId: env.orgId, ...payload }),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: (commentId: string) => client.comments.remove({ orgId: env.orgId, commentId }),
    onSuccess: refresh,
    onError,
  });
  const members = useQuery({
    ...orpc.comments.mentionable.queryOptions({ input: { orgId: env.orgId } }),
    staleTime: 5 * 60_000,
  });
  const comments = query.data ?? [];
  const threads = useMemo(() => buildThreads(comments), [comments]);
  return {
    comments,
    threads,
    loading: query.isLoading,
    create,
    update,
    remove,
    mentionables: members.data,
  };
}

function visibleThreads(threads: CommentThread[], filter: CommentFilter) {
  return threads.filter((thread) =>
    filter === "all"
      ? true
      : filter === "open"
        ? !thread.root.resolvedAt
        : Boolean(thread.root.resolvedAt),
  );
}

// ---------------------------------------------------------------------------
// Header button, panel, pins
// ---------------------------------------------------------------------------

export function CommentsButton() {
  const ui = useCommentUi();
  const env = useEnv();
  const { data } = useQuery({
    ...orpc.comments.list.queryOptions({ input: { orgId: env.orgId, documentId: env.documentId } }),
    refetchInterval: 20_000,
  });
  if (!ui) return null;
  const open = (data ?? []).filter((comment) => !comment.parentId && !comment.resolvedAt).length;
  return (
    <button
      type="button"
      onClick={() => ui.setOpen(!ui.open)}
      title="Kommentare"
      aria-pressed={ui.open}
      className={cn(
        "relative inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 [&_svg]:size-4",
        ui.open && "bg-brand-50 text-brand-700 hover:bg-brand-100",
      )}
    >
      <MessageSquare />
      <span className="hidden 2xl:inline">Kommentare</span>
      {open > 0 ? (
        <span className="rounded-full bg-brand-600 px-1.5 text-[11px] leading-5 font-semibold text-white">
          {open}
        </span>
      ) : null}
    </button>
  );
}

export function CommentsPanel() {
  const ui = useCommentUi()!;
  const env = useEnv();
  const store = useEditorApi();
  const video = useVideoApi();
  const { can } = useOrgRole(env.orgId);
  const pages = useEditor((s) => s.doc.pages);
  const kind = useEditor((s) => s.doc.kind);
  const pageIndex = useEditor((s) => s.pageIndex);
  const { threads, loading, create, update, remove, mentionables } = useDocumentComments();
  const shown = visibleThreads(threads, ui.filter);
  const isVideo = kind === "video";
  const canComment = can("comment.create");
  // Opened at a thread (link from a notification): show it even when it is done.
  const activeThread = threads.find((thread) => thread.root.id === ui.active);
  const activeLoaded = Boolean(activeThread);
  const activeResolved = Boolean(activeThread?.root.resolvedAt);
  const activePage = pages.findIndex((page) => page.id === activeThread?.root.pageId);
  const { setFilter, active } = ui;
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !activeLoaded || revealed.current === active) return;
    revealed.current = active;
    if (activeResolved) setFilter("all");
    if (activePage >= 0 && activePage !== store.getState().pageIndex) {
      store.getState().setPage(activePage);
    }
    // After the panel and the canvas settled (the canvas fits the page on load).
    setTimeout(
      () =>
        document
          .querySelector(`[data-thread="${active}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" }),
      200,
    );
  }, [active, activeLoaded, activeResolved, activePage, setFilter, store]);

  const focus = (thread: CommentThread) => {
    ui.setActive(thread.root.id);
    const index = pages.findIndex((page) => page.id === thread.root.pageId);
    if (index >= 0 && index !== pageIndex) store.getState().setPage(index);
    if (video && thread.root.timeMs !== null) video.getState().seek(thread.root.timeMs);
  };
  const actions = {
    reply: (parentId: string, body: string) => create.mutateAsync({ parentId, body }),
    resolve: (rootId: string, resolved: boolean) =>
      update.mutateAsync({ commentId: rootId, resolved }),
    edit: (commentId: string, body: string) => update.mutateAsync({ commentId, body }),
    remove: (commentId: string) => remove.mutateAsync(commentId),
    canRemove: (comment: DocumentComment) => comment.mine || can("document.update"),
  };
  const now = () => (video ? Math.round(video.getState().time) : undefined);
  const counts = {
    open: threads.filter((thread) => !thread.root.resolvedAt).length,
    resolved: threads.filter((thread) => thread.root.resolvedAt).length,
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-zinc-100 px-4">
        <h2 className="text-sm font-semibold text-zinc-900">Kommentare</h2>
        <button
          type="button"
          title="Schließen"
          onClick={() => ui.setOpen(false)}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="space-y-2 border-b border-zinc-100 p-3">
        {canComment ? (
          <button
            type="button"
            onClick={() => {
              ui.setDraft(null);
              ui.setPlacing(!ui.placing);
            }}
            className={cn(
              "inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border text-sm font-medium [&_svg]:size-4",
              ui.placing
                ? "border-brand-600 bg-brand-600 text-white"
                : "border-zinc-200 text-zinc-700 hover:bg-zinc-50",
            )}
          >
            <MapPin />
            {ui.placing ? "Klicke auf die Stelle …" : "Kommentar an eine Stelle setzen"}
          </button>
        ) : null}
        <div className="flex rounded-lg bg-zinc-100 p-0.5 text-xs">
          {(["open", "resolved", "all"] as const).map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => ui.setFilter(entry)}
              className={cn(
                "flex-1 rounded-md py-1 text-zinc-600",
                ui.filter === entry && "bg-white font-medium text-zinc-900 shadow-sm",
              )}
            >
              {entry === "open"
                ? `Offen (${counts.open})`
                : entry === "resolved"
                  ? `Erledigt (${counts.resolved})`
                  : "Alle"}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {ui.draft && canComment ? (
          <div className="rounded-xl border border-brand-300 bg-brand-50/50 p-3">
            <p className="mb-2 text-xs font-medium text-brand-800">
              Neuer Kommentar an der markierten Stelle
              {isVideo && ui.draft.timeMs !== undefined
                ? ` (${formatDuration(ui.draft.timeMs)})`
                : ""}
            </p>
            <CommentComposer
              autoFocus
              onCancel={() => ui.setDraft(null)}
              mentionables={mentionables}
              placeholder="Kommentar schreiben … (@ erwähnt jemanden)"
              onSubmit={async (body) => {
                const comment = await create.mutateAsync({ body, ...ui.draft! });
                ui.setDraft(null);
                ui.setActive(comment.id);
                ui.setFilter("open");
              }}
            />
          </div>
        ) : null}
        {loading ? (
          <div className="flex justify-center py-6 text-zinc-400">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : shown.length === 0 && !ui.draft ? (
          <div className="rounded-lg bg-zinc-50 p-4 text-center text-xs text-zinc-500">
            <MessageSquarePlus className="mx-auto mb-2 size-6 text-zinc-300" />
            {ui.filter === "resolved"
              ? "Noch nichts erledigt."
              : "Noch keine offenen Kommentare. Setze einen an eine Stelle der Seite oder schreibe unten einen allgemeinen."}
          </div>
        ) : (
          shown.map((thread) => (
            <div key={thread.root.id} data-thread={thread.root.id}>
              <CommentThreadView
                thread={thread}
                active={ui.active === thread.root.id}
                onActivate={() => focus(thread)}
                label={threadLabel(thread.root, pages, isVideo)}
                mentionables={mentionables}
                actions={canComment ? actions : {}}
              />
            </div>
          ))
        )}
      </div>
      {canComment ? (
        // Bottom padding: the activity button floats in the corner.
        <div className="border-t border-zinc-100 p-3 pb-14">
          <CommentComposer
            placeholder={
              isVideo
                ? "Kommentar zur aktuellen Stelle im Video … (@ erwähnt jemanden)"
                : "Allgemeiner Kommentar zur Datei … (@ erwähnt jemanden)"
            }
            mentionables={mentionables}
            onSubmit={async (body) => {
              const comment = await create.mutateAsync({
                body,
                pageId: pages[pageIndex]?.id,
                ...(isVideo ? { timeMs: now() } : {}),
              });
              ui.setActive(comment.id);
              ui.setFilter("open");
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** How close (ms) the playhead has to be for a video comment's pin to show. */
const VIDEO_PIN_WINDOW = 1500;

/** Pins of the page's comment threads, drawn in page coordinates (inside the canvas). */
export function EditorCommentPins({ zoom }: { zoom: number }) {
  const ui = useCommentUi();
  const env = useEnv();
  const store = useEditorApi();
  const video = useVideoApi();
  const page = useEditor((s) => s.doc.pages[s.pageIndex]);
  const single = useEditor((s) => s.doc.pages.length === 1);
  const time = useVideoMaybe((s) => s.time, 0);
  const { data } = useQuery({
    ...orpc.comments.list.queryOptions({ input: { orgId: env.orgId, documentId: env.documentId } }),
    refetchInterval: 20_000,
    enabled: Boolean(ui?.open),
  });
  if (!ui?.open || !page) return null;
  const threads = visibleThreads(buildThreads(data ?? []), ui.filter).filter(
    (thread) =>
      thread.root.x !== null &&
      thread.root.y !== null &&
      (thread.root.pageId === page.id || (single && !thread.root.pageId)) &&
      (!video ||
        thread.root.timeMs === null ||
        ui.active === thread.root.id ||
        Math.abs(time - thread.root.timeMs) <= VIDEO_PIN_WINDOW),
  );
  return (
    <>
      {threads.map((thread) => (
        <CommentPin
          key={thread.root.id}
          x={thread.root.x!}
          y={thread.root.y!}
          number={thread.number}
          scale={zoom}
          active={ui.active === thread.root.id}
          resolved={Boolean(thread.root.resolvedAt)}
          onClick={() => {
            ui.setActive(thread.root.id);
            store.getState().select([]);
            document
              .querySelector(`[data-thread="${thread.root.id}"]`)
              ?.scrollIntoView({ block: "nearest" });
          }}
        />
      ))}
      {ui.draft && ui.draft.pageId === page.id ? (
        <CommentPin x={ui.draft.x} y={ui.draft.y} scale={zoom} draft />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Share links
// ---------------------------------------------------------------------------

const EXPIRY_OPTIONS = [
  { value: "", label: "Unbegrenzt gültig" },
  { value: "7", label: "7 Tage gültig" },
  { value: "30", label: "30 Tage gültig" },
  { value: "90", label: "90 Tage gültig" },
];

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Link kopiert");
  } catch {
    toast.error("Kopieren nicht möglich – bitte den Link markieren und kopieren");
  }
}

function ShareLinkRow({ link }: { link: ShareLink }) {
  const env = useEnv();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.shares.key() });
  const update = useMutation({
    mutationFn: (access: ShareAccess) =>
      client.shares.update({ orgId: env.orgId, linkId: link.id, access }),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const revoke = useMutation({
    mutationFn: () => client.shares.revoke({ orgId: env.orgId, linkId: link.id }),
    onSuccess: () => {
      void refresh();
      toast.success("Link widerrufen – er funktioniert ab sofort nicht mehr");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const expired = link.expiresAt !== null && link.expiresAt.getTime() < Date.now();
  const inactive = Boolean(link.revokedAt) || expired;
  return (
    <li className={cn("space-y-2 rounded-xl border border-zinc-200 p-3", inactive && "opacity-60")}>
      <div className="flex items-center gap-2">
        <Link2 className="size-4 shrink-0 text-zinc-400" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-900">
          {link.label ?? "Link"}
        </span>
        {inactive ? (
          <span className="text-xs text-zinc-500">
            {link.revokedAt ? "Widerrufen" : "Abgelaufen"}
          </span>
        ) : (
          <Select
            className="h-7 w-auto text-xs"
            value={link.access}
            disabled={update.isPending}
            onChange={(event) => update.mutate(event.target.value as ShareAccess)}
            aria-label="Rechte des Links"
          >
            {SHARE_ACCESS.map((access) => (
              <option key={access} value={access}>
                {SHARE_ACCESS_LABELS[access]}
              </option>
            ))}
          </Select>
        )}
      </div>
      {inactive ? null : (
        <div className="flex gap-2">
          <Input
            readOnly
            value={link.url}
            className="h-8 min-w-0 flex-1 font-mono text-xs"
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button size="sm" variant="secondary" onClick={() => void copy(link.url)}>
            <Copy /> Kopieren
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <span className="min-w-0 flex-1 truncate">
          {link.createdBy?.name ?? "Unbekannt"} · {formatDate(link.createdAt)}
          {link.expiresAt ? ` · gültig bis ${formatDate(link.expiresAt)}` : ""} ·{" "}
          {link.viewCount === 0
            ? "noch nicht geöffnet"
            : `${link.viewCount}× geöffnet${link.lastViewedAt ? `, zuletzt ${formatRelative(link.lastViewedAt)}` : ""}`}
        </span>
        {inactive ? null : (
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            className="shrink-0 font-medium text-red-600 hover:underline"
          >
            Widerrufen
          </button>
        )}
      </div>
    </li>
  );
}

export function ShareDialog({
  open,
  onOpenChange,
  beforeShare,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  beforeShare: () => Promise<unknown>;
}) {
  const env = useEnv();
  const queryClient = useQueryClient();
  const kind = useEditor((s) => s.doc.kind);
  const links = useQuery({
    ...orpc.shares.list.queryOptions({
      input: { orgId: env.orgId, documentId: env.documentId },
    }),
    enabled: open,
  });
  const [label, setLabel] = useState("");
  const [access, setAccess] = useState<ShareAccess>("comment");
  const [expiry, setExpiry] = useState("");
  const create = useMutation({
    mutationFn: async () => {
      await beforeShare();
      return client.shares.create({
        orgId: env.orgId,
        documentId: env.documentId,
        access,
        expiresInDays: expiry ? Number(expiry) : null,
        ...(label.trim() ? { label: label.trim() } : {}),
      });
    },
    onSuccess: (link) => {
      void queryClient.invalidateQueries({ queryKey: orpc.shares.key() });
      setLabel("");
      void copy(link.url);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const active = (links.data ?? []).filter((link) => !link.revokedAt);
  const old = (links.data ?? []).filter((link) => link.revokedAt);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Teilen"
        description="Wer den Link hat, sieht die Datei im Browser – ohne Konto. Du kannst ihn jederzeit widerrufen."
        className="max-w-lg"
      >
        <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1">
          <form
            className="space-y-3 rounded-xl bg-zinc-50 p-4"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Für wen? (optional)">
              <Input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="z. B. „Kunde Müller“ oder „Druckerei“"
                maxLength={120}
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={access}
                onChange={(event) => setAccess(event.target.value as ShareAccess)}
                aria-label="Rechte"
              >
                {SHARE_ACCESS.map((entry) => (
                  <option key={entry} value={entry}>
                    {SHARE_ACCESS_LABELS[entry]}
                  </option>
                ))}
              </Select>
              <Select
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
                aria-label="Gültigkeit"
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            {kind === "video" ? (
              <p className="text-xs text-zinc-500">
                Videos zeigt der Link als zuletzt exportiertes MP4 – nach Änderungen einfach neu
                exportieren.
              </p>
            ) : null}
            <Button type="submit" loading={create.isPending} className="w-full">
              <Share2 /> Link erstellen & kopieren
            </Button>
          </form>
          {links.isLoading ? (
            <div className="flex justify-center py-4 text-zinc-400">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : active.length > 0 ? (
            <ul className="space-y-2">
              {active.map((link) => (
                <ShareLinkRow key={link.id} link={link} />
              ))}
            </ul>
          ) : (
            <p className="text-center text-sm text-zinc-500">Noch keine Links für diese Datei.</p>
          )}
          {old.length > 0 ? (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-medium text-zinc-500">
                Widerrufene Links ({old.length})
              </summary>
              <ul className="mt-2 space-y-2">
                {old.map((link) => (
                  <ShareLinkRow key={link.id} link={link} />
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
